import { useEffect, useState } from "react";
import { CRMPermissionContext } from "../context/CRMPermissionContext";
import { useGetCrmUsersQuery, useMeQuery } from "@/generated";
import { useRouter } from "next/router";

interface ICRMPermissionProvider {
  children: React.ReactNode;
}

const CRMPermissionProvider = ({ children }: ICRMPermissionProvider) => {
  const [permissions, setPermissions] = useState<string[]>();
  const [crmRoles, setCrmRoles] = useState<string[]>();
  const [campaignSlug, setCampaignSlug] = useState<string | null>(null);
  const router = useRouter();
  const { data: meData, loading: userLoading } = useMeQuery();

  const [allowedContactSources, setAllowedContactSources] =
    useState<string[]>();

  const { data: campaignData, loading: campaignLoading } = useGetCrmUsersQuery({
    skip: !meData?.me?.id,
    fetchPolicy: "cache-and-network",
    variables: {
      filters: {
        slug: {
          eq: router?.query?.["campaign-name"]?.toString() ?? undefined,
        },
      },
      pagination: {
        limit: -1,
      },
      usersWithRolesPagination: {
        limit: -1,
      },
      usersWithRolesFilters: {
        user: {
          id: {
            eq: meData?.me?.id,
          },
        },
      },
    },
  });

  // In case, if campaign is not provided, the permission will be fetched, based on CRM Profile, from User
  useEffect(() => {
    if (!userLoading && !campaignLoading) {
      const userPermissions =
        campaignData?.campaigns?.data?.flatMap((campaign) =>
          campaign?.attributes?.users_with_roles?.flatMap((userWithRoles) =>
            userWithRoles?.roles?.data?.flatMap(
              (role) => role?.attributes?.permissions
            )
          )
        ) ?? [];
      setCrmRoles(
        campaignData?.campaigns?.data?.[0]?.attributes?.users_with_roles?.[0]?.roles?.data?.map(
          (item: any) => item?.id
        ) ?? []
      );

      const allowedContactSources = new Set(
        campaignData?.campaigns?.data?.flatMap((campaign) =>
          campaign?.attributes?.users_with_roles?.flatMap((userWithRoles) =>
            userWithRoles?.roles?.data?.flatMap((contactSource) =>
              contactSource?.attributes?.contact_sources?.data?.flatMap(
                (contactSourceItem) => contactSourceItem?.id
              )
            )
          )
        )
      );

      const uniqueAllowedContactSources = Array.from(
        allowedContactSources
      ).filter((item) => item !== undefined && item !== null);

      setAllowedContactSources(uniqueAllowedContactSources);

      userPermissions?.length > 0
        ? setPermissions(userPermissions)
        : setPermissions(["none"]);
    }
  }, [campaignData?.campaigns?.data, campaignLoading, userLoading]);

  return (
    <CRMPermissionContext.Provider
      value={{
        permissions: permissions ?? [],
        setCampaignSlug,
        crmRoles: crmRoles ?? [],
        allowedContactSources: allowedContactSources ?? [],
      }}
    >
      {children}
    </CRMPermissionContext.Provider>
  );
};

export default CRMPermissionProvider;
